/**
 * User Edit Dialog Component
 * Modal dialog for editing user details
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminService, { UserDetailResponse, UpdateUserRequest } from '../../services/AdminService';
import { translateError } from '../../errors';
import { Institution } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';
import { InstitutionTransferDialog } from './InstitutionTransferDialog';

interface UserEditDialogProps {
  userId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const UserEditDialog: React.FC<UserEditDialogProps> = ({
  userId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<UserDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [institutionsError, setInstitutionsError] = useState<string | null>(null);
  const [showTransferDialog, setShowTransferDialog] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });

  const loadUser = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      const userData = await AdminService.getUser(userId);
      setUser(userData);
      setFormData({
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
      });
    } catch (err) {
      setError(translateError(err, t, 'admin.userEditDialog.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    if (isOpen && userId) {
      loadUser();
    }
  }, [isOpen, userId, loadUser]);

  const loadInstitutions = useCallback(async () => {
    try {
      const list = await AdminService.listInstitutions();
      setInstitutions(list);
      setInstitutionsError(null);
    } catch (err) {
      // Component-scoped fallback key, like every other translateError() call
      // site in this file — not the `errors.*` key directly. It also happens
      // to be the exact sentence already shown next to the disabled transfer
      // button (line ~241), so reusing it keeps both places in sync.
      const msg = translateError(err, t, 'admin.institutionTransfer.institutionsLoadFailed');
      console.error('[UserEditDialog] loadInstitutions failed:', err);
      setInstitutionsError(msg);
      setInstitutions([]);
    }
    // `t` joins the deps because the fallback is translated here now; the
    // effect below re-runs on a language switch as a result, which only
    // re-fetches a list this dialog already refetches on every open.
  }, [t]);

  useEffect(() => {
    if (isOpen && currentUser?.is_superuser) {
      loadInstitutions();
    }
  }, [isOpen, currentUser, loadInstitutions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) return;

    try {
      setSaving(true);
      setError(null);

      const updateData: UpdateUserRequest = {};

      if (formData.first_name !== user?.first_name) {
        updateData.first_name = formData.first_name;
      }
      if (formData.last_name !== user?.last_name) {
        updateData.last_name = formData.last_name;
      }
      if (formData.email !== user?.email) {
        updateData.email = formData.email;
      }

      if (Object.keys(updateData).length === 0) {
        onClose();
        return;
      }

      await AdminService.updateUser(userId, updateData);
      onSuccess();
      onClose();
    } catch (err) {
      setError(translateError(err, t, 'admin.userEditDialog.failedUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {t('admin.userEditDialog.title')}
                  </h3>

                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Error Message */}
                      {error && (
                        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                          {error}
                        </div>
                      )}

                      {/* First Name */}
                      <div>
                        <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('admin.userEditDialog.firstNameLabel')}
                        </label>
                        <input
                          type="text"
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Last Name */}
                      <div>
                        <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('admin.userEditDialog.lastNameLabel')}
                        </label>
                        <input
                          type="text"
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('admin.userEditDialog.emailLabel')}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* User Info */}
                      {user && (
                        <div className="bg-gray-50 p-3 rounded-lg text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="col-span-2 flex items-center justify-between">
                              <div>
                                <span className="text-gray-500">
                                  {t('admin.userEditDialog.institutionLabel')}:
                                </span>
                                <span className="ml-2 font-medium">{user.institution_name}</span>
                              </div>
                              {currentUser?.is_superuser && currentUser.id !== user.id && (
                                <button
                                  type="button"
                                  onClick={() => setShowTransferDialog(true)}
                                  disabled={institutionsError !== null || institutions.length === 0}
                                  className="text-sm text-blue-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                                >
                                  {t('admin.institutionTransfer.button')}
                                </button>
                              )}
                            </div>
                            {institutionsError && (
                              <div className="col-span-2 text-xs text-red-600 mt-1">
                                {t('admin.institutionTransfer.institutionsLoadFailed')}
                              </div>
                            )}
                            <div>
                              <span className="text-gray-500">{t('admin.userEditDialog.statusLabel')}:</span>
                              <span className="ml-2 font-medium">{user.status}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-500">{t('admin.userEditDialog.rolesLabel')}:</span>
                              <span className="ml-2 font-medium">
                                {user.roles.map(r => r.display_name).join(', ')}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={saving || loading}
                className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? t('admin.userEditDialog.saving') : t('admin.userEditDialog.saveChanges')}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('admin.userEditDialog.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    {showTransferDialog && user && currentUser?.id !== user.id && (
      <InstitutionTransferDialog
        user={user}
        institutions={institutions}
        isOpen={showTransferDialog}
        onClose={() => setShowTransferDialog(false)}
        onSuccess={(_toast) => {
          onSuccess();
          setShowTransferDialog(false);
        }}
      />
    )}
    </>
  );
};
